// 法律条款页共用的外壳（隐私政策 / 服务条款）。
// 原来两个页面各写一套顶栏，且全是硬编码英文；现在统一到这里：
//   · 顶栏带「返回官网」+ 语言切换（EN / 中文），语言从 localStorage 读，跟着首页走
//   · 正文按语言渲染两套内容
//   · 中文译文页顶部统一挂一条「以英文版为准」的说明
import React from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Container,
  Divider,
  Heading,
  HStack,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { LangSwitch } from './LangSwitch';

export const LegalShell = ({ lang, setLang, title, updated, notice, children, footerExtra }) => (
  <Box minH="100vh" bg="gray.50">
    <Box bg="white" borderBottom="1px solid" borderColor="gray.200" py={4} position="sticky" top={0} zIndex={10}>
      <Container maxW="860px">
        <HStack justify="space-between" spacing={4}>
          <Link
            as={RouterLink}
            to="/"
            fontWeight="semibold"
            color="blue.600"
            fontSize="sm"
            _hover={{ color: 'blue.700' }}
          >
            {lang === 'zh' ? '← 返回 Bizsprite 官网' : '← Back to Bizsprite'}
          </Link>
          <LangSwitch lang={lang} setLang={setLang} />
        </HStack>
      </Container>
    </Box>

    <Container maxW="860px" py={{ base: 10, md: 14 }} px={6}>
      <VStack align="start" spacing={8}>
        <Box>
          <Text fontSize="sm" color="gray.500" mb={1}>
            AIKERR.COM
          </Text>
          {/* Chakra 的 Heading 默认渲染成 h2，法务页此前整页没有 h1。
              这里显式指定 h1，下面的 LegalSection 用 h2，标题层级才成立。 */}
          <Heading as="h1" size="xl" color="gray.900" mb={2}>
            {title}
          </Heading>
          {updated ? (
            <Text color="gray.500" fontSize="sm">
              {updated}
            </Text>
          ) : null}
        </Box>

        <Divider />

        {notice}

        {children}

        <Divider />
        <HStack spacing={4} color="gray.400" fontSize="sm" flexWrap="wrap">
          <Text>{lang === 'zh' ? '© 2026 Aiker Inc. 保留所有权利。' : '© 2026 Aiker Inc. All rights reserved.'}</Text>
          {footerExtra}
        </HStack>
      </VStack>
    </Container>
  </Box>
);

/* 中文译文说明条：只在中文版出现 */
export const TranslationNotice = ({ lang }) =>
  lang === 'zh' ? (
    <Alert status="warning" borderRadius="md" alignItems="flex-start">
      <AlertIcon mt="2px" />
      <Text fontSize="sm" lineHeight="tall">
        <strong>本页为英文原文的中文译文，仅供参考。</strong>
        如中文译文与英文版存在歧义或不一致，以英文版为准。
      </Text>
    </Alert>
  ) : null;

/* 英文原文说明条（服务条款用，保留原来的仲裁提示） */
export const TermsNotice = ({ lang }) =>
  lang === 'zh' ? (
    <Alert status="info" borderRadius="md" alignItems="flex-start">
      <AlertIcon mt="2px" />
      <Text fontSize="sm" lineHeight="tall">
        <strong>本页为面向中国客户的《Bizsprite AI 语音客服业务服务条款》。</strong>
        英文版《Terms and Conditions》（含仲裁条款、加州用户告知等）适用于美国主体，两者适用范围不同；
        如你的订单或服务确认单另有约定，以订单约定为准。
      </Text>
    </Alert>
  ) : (
    <Alert status="warning" borderRadius="md" alignItems="flex-start">
      <AlertIcon mt="2px" />
      <Text fontSize="sm" lineHeight="tall">
        <strong>Please read these Terms and Conditions carefully.</strong> They contain an agreement to
        arbitrate and other important information regarding your legal rights, remedies, and obligations.
        The agreement to arbitrate requires (with limited exception) that you submit claims to binding and
        final arbitration on an individual basis.
      </Text>
    </Alert>
  );

/* 条款小标题 */
export const LegalSection = ({ title, children }) => (
  <Box>
    <Heading as="h2" size="sm" mb={3} color="gray.800" textTransform="uppercase" letterSpacing="wide">
      {title}
    </Heading>
    {children}
  </Box>
);

/* 正文段落：统一行高与颜色，省得每段重复写 */
export const P = ({ children, ...rest }) => (
  <Text color="gray.700" lineHeight="tall" {...rest}>
    {children}
  </Text>
);

/* 「小标题 + 正文」的一行，用于条款里的分项说明 */
export const Item = ({ label, children }) => (
  <Text color="gray.700" lineHeight="tall">
    <Text as="span" fontWeight="semibold">
      {label}
    </Text>{' '}
    {children}
  </Text>
);
